import React, { useEffect, useRef, useState } from "react";
import LabeledTextField from "../../Components/Common/LabeledTextField";
import LoadingButton from "../../Components/Common/LoadingButton";
import StatusAlert from "../../Components/Common/StatusAlert";
import { SelectField } from "../../Components/Form/fields";
import { useTranslation } from "../../context/Internationalization";
import useGraphql from "../../Hooks/useGraphql";
import { SAVE_CLUB_BILLING_TAX_IDENTITY, SAVE_CLUB_INVOICE_DETAILS } from "../../graphql/billingGraphql";
import SectionHeader from "../../shadcn/components/pro-blocks/application/section-headers/section-header";

export default function BillingTaxIdentityForm({ clubId, onSaved, invoice, onAccessDenied }) {
  const { t } = useTranslation();
  const { axiosGraphQL } = useGraphql();
  const [country, setCountry] = useState(invoice?.country || "GR");
  const [nationalTaxId, setNationalTaxId] = useState(invoice?.nationalTaxId || "");
  const [vatId, setVatId] = useState(invoice?.vatId || "");
  const [busy, setBusy] = useState(false);
  const [blocked, setBlocked] = useState(null);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({ national: false, vat: false });
  const pending = useRef(false);
  const mounted = useRef(false);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const submit = async event => {
    event.preventDefault();
    if (pending.current || blocked) return;
    const national = nationalTaxId.trim().replace(/\s/g, "").toUpperCase()
      .replace(country === "GR" ? /^(GR|EL)/ : /^CY/, "");
    const vat = vatId.trim().replace(/\s/g, "").toUpperCase().replace(/^CY/, "");
    const nationalInvalid = !(country === "GR" ? /^\d{9}$/ : /^\d{8}[A-Z]$/).test(national);
    const vatInvalid = country === "CY" && Boolean(vat) && !/^\d{8}[A-Z]$/.test(vat);
    setFieldErrors({ national: nationalInvalid, vat: vatInvalid });
    if (nationalInvalid || vatInvalid) {
      setError("billing_tax_identity_invalid"); return;
    }
    pending.current = true; setBusy(true); setError(null);
    try {
      const response = await axiosGraphQL({ query: invoice ? SAVE_CLUB_INVOICE_DETAILS : SAVE_CLUB_BILLING_TAX_IDENTITY,
        variables: { clubId, ...(invoice ? { invoiceId: invoice.invoiceId } : { country }), nationalTaxId: national, vatId: country === "CY" && vat ? `CY${vat}` : null } });
      if (!mounted.current) return;
      if (response?.data?.data?.[invoice ? "saveClubInvoiceDetails" : "saveClubBillingTaxIdentity"] !== true) throw new Error("save_failed");
      if (invoice) setBlocked("saved");
      onSaved();
    } catch (failure) {
      if (!mounted.current) return;
      const codes = failure?.codes || [];
      const denied = [401, 403].includes(failure?.statusCode || failure?.response?.status) ||
        codes.some(code => ["FORBIDDEN_BILLING_ACCESS", "FORBIDDEN_CLUB_ACCESS", "UNAUTHORIZED_CLUB_ACCESS",
          "BILLING_ROLLOUT_DISABLED", "UNAUTHENTICATED", "UNAUTHORIZED", "FORBIDDEN"].includes(code));
      if (invoice && denied) {
        setBlocked("denied"); setNationalTaxId(""); setVatId("");
        onAccessDenied?.(); return;
      }
      if (invoice && codes.includes("BILLING_INVOICE_DETAILS_REVIEW_REQUIRED")) setBlocked("review");
      setError(codes.includes("BILLING_TAX_IDENTITY_INVALID") ? "billing_tax_identity_invalid"
        : codes.includes("BILLING_INVOICE_DETAILS_BUSY") ? "billing_invoice_details_busy"
        : codes.includes("BILLING_INVOICE_DETAILS_REVIEW_REQUIRED") ? "billing_invoice_details_review"
        : codes.includes("BILLING_IDENTITY_CHECKOUT_IN_PROGRESS") ? "billing_tax_identity_locked"
          : "billing_tax_identity_save_error");
    } finally {
      pending.current = false;
      if (mounted.current) setBusy(false);
    }
  };
  if (blocked === "denied") return <StatusAlert severity="error">{t("billing_invoice_details_load_error")}</StatusAlert>;
  return <form noValidate onSubmit={submit} className="flex max-w-xl flex-col gap-4">
    <SectionHeader size="lg" title={t(invoice ? "billing_invoice_details_open" : "billing_tax_identity_title")} />
    <p>{t(invoice ? "billing_invoice_details_explanation" : "billing_tax_identity_explanation")}</p>
    {error && <StatusAlert severity="error">{t(error)}</StatusAlert>}
    <SelectField label={t("billing_tax_country")} name="country" value={country} disabled={busy || Boolean(blocked) || Boolean(invoice)}
      options={[{ value: "GR", text: t("billing_tax_country_gr") }, { value: "CY", text: t("billing_tax_country_cy") }]}
      onChange={event => { setCountry(event.target.value); setNationalTaxId(""); setVatId(""); setError(null); setFieldErrors({ national: false, vat: false }); }} />
    <LabeledTextField required disabled={busy || Boolean(blocked)} label={t(country === "GR" ? "billing_tax_afm" : "billing_tax_tic")}
      value={nationalTaxId} aria-invalid={fieldErrors.national}
      onChange={event => { setNationalTaxId(event.target.value); setFieldErrors(previous => ({ ...previous, national: false })); }}
      helperText={t(fieldErrors.national ? "billing_tax_identity_invalid" : country === "GR" ? "billing_tax_afm_hint" : "billing_tax_tic_hint")}
      maxLength={32} autoComplete="off" />
    {country === "CY" && <LabeledTextField disabled={busy || Boolean(blocked)} label={t("billing_tax_vat_optional")}
      value={vatId} aria-invalid={fieldErrors.vat}
      onChange={event => { setVatId(event.target.value); setFieldErrors(previous => ({ ...previous, vat: false })); }}
      helperText={t(fieldErrors.vat ? "billing_tax_identity_invalid" : "billing_tax_vat_hint")} maxLength={32} autoComplete="off" />}
    <LoadingButton type="submit" loading={busy} disabled={Boolean(blocked)}>{t(busy ? "billing_tax_saving" : invoice ? "billing_invoice_details_save" : "billing_tax_continue")}</LoadingButton>
  </form>;
}
